import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, Download, FileText, Loader2, Lock, Plus, Send, Users, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { AddressInput } from '../components/AddressInput';
import { validatePersonFullName } from '../lib/personNames';
import { ManualTemplateBanner } from '../components/ManualTemplateBanner';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const toApiUrl = (path) => (API_URL ? `${API_URL}${path}` : path);

const hostedDocuments = [
  {
    label: 'How to Rent Guide',
    note: 'Required at tenancy start. Government version available in-app.',
    helper: 'Government guide. Always ensure this is the current version - check gov.uk if in doubt.',
    fileName: 'how_to_rent_guide_current.pdf',
    govUrl: 'https://www.gov.uk/government/publications/how-to-rent',
    govLabel: 'gov.uk How to Rent source',
  },
  {
    label: "Renters' Rights Act Information Sheet 2026",
    note: 'Landlords are required to deliver certain documents within set timeframes.',
    helper: 'Must be provided to all existing tenants by 31 May 2026.',
    fileName: 'renters_rights_act_info_sheet_2026.pdf',
    govUrl: 'https://www.gov.uk/government/publications/the-renters-rights-act-information-sheet-2026',
    govLabel: 'gov.uk Renters\' Rights information sheet',
  },
];

const uploadDocuments = [
	{ label: 'Right to Rent Document', note: 'Share Right to Rent evidence with tenants where appropriate and keep auditable delivery records.' },
  { label: 'Gas Safety Certificate (CP12)', note: 'Required before tenancy starts and within 28 days of each annual renewal. Gas Safety (Installation and Use) Regulations 1998.' },
  { label: 'EPC — Energy Performance Certificate', note: 'Required before tenancy starts. Minimum E rating. Valid 10 years.' },
  { label: 'EICR — Electrical Installation Condition Report', note: 'Required at tenancy start, renewed every 5 years. Electrical Safety Standards Regulations 2020.' },
  { label: 'Deposit Prescribed Information', note: 'Must be provided within 30 days of receiving the deposit. Housing Act 2004. Include your deposit scheme certificate.' },
  { label: 'Tenancy Agreement', note: 'Strongly recommended at the start of every tenancy.' },
  { label: 'Legionella Risk Assessment', note: 'Required where property has stored or complex water systems.' },
  { label: 'HMO Licence', note: 'Required for Houses in Multiple Occupation.' },
  { label: 'Fire Risk Assessment', note: 'Required for HMOs and buildings of 7 or more storeys.' },
  { label: 'Section 13 Rent Increase Notice', note: 'Formal notice of rent increase. Must give correct notice period.' },
  { label: 'Other / General Document', note: 'General correspondence, notices, or supporting documents.' },
];

const documentGroups = [
  {
    key: 'hosted',
    label: 'Standard Documents',
    items: hostedDocuments.map((doc) => ({ ...doc, source: 'hosted' })),
  },
  {
    key: 'upload',
    label: 'Upload Your Own Document',
    items: uploadDocuments.map((doc) => ({ ...doc, source: 'upload' })),
  },
];

const allDocumentOptions = documentGroups.flatMap((group) => group.items);

// ─── Composable template system ────────────────────────────────────────────
// Each document type declares an intro sentence and an action block key.
// Templates are assembled by buildTemplate() — landlords can edit freely.

const ACTION_BLOCKS = {
  informational:
    `Please take a moment to read through this document.\nIf you have any questions, feel free to get in touch.`,

  formal_notice:
    `Please read this document carefully, as it contains important information about your tenancy.\nIf anything is unclear, please seek advice or get in touch.`,

  record_review:
    `Please review this document and keep a copy for your records.\nIf you have any questions, feel free to get in touch.`,
};

const DOCUMENT_TEMPLATE_CONFIG = {
  'How to Rent Guide': {
    intro: "Your landlord is required to give you this guide before your tenancy starts.",
    actionBlock: 'informational',
  },
  "Renters' Rights Act Information Sheet 2026": {
    intro: "Your landlord is required to provide you with this information sheet under the Renters' Rights Act 2026.",
    actionBlock: 'informational',
  },
  'Gas Safety Certificate (CP12)': {
    intro: "Your landlord is required by law to give you a copy of the gas safety certificate each year.",
    actionBlock: 'record_review',
  },
  'EPC \u2014 Energy Performance Certificate': {
    intro: "Your landlord is required to provide you with an Energy Performance Certificate before your tenancy begins.",
    actionBlock: 'record_review',
  },
  'EICR \u2014 Electrical Installation Condition Report': {
    intro: "Your landlord is required to provide you with this electrical safety certificate at the start of your tenancy.",
    actionBlock: 'record_review',
  },
  'Deposit Prescribed Information': {
    intro: "Your deposit has been protected and this document provides the required protection information.",
    actionBlock: 'record_review',
  },
  'Tenancy Agreement': {
    intro: "Please read this document carefully before signing and keep a copy for your records.",
    actionBlock: 'record_review',
  },
  'Section 13 Rent Increase Notice': {
    intro: "This is a formal notice that your landlord intends to increase your rent. You may have the right to challenge the proposed increase at a First-tier Tribunal.",
    actionBlock: 'formal_notice',
  },
  'Right to Rent Document': {
    intro: "Your landlord is sharing this document with you for your records.",
    actionBlock: 'record_review',
  },
  default: {
    intro: '',
    actionBlock: 'record_review',
  },
};

/**
 * Build the default email body for a given document type.
 * All placeholders ([Tenant Name(s)], [Property Address], etc.) are substituted
 * by the generatedTemplate memo — this function works with raw tokens.
 */
function buildTemplate(documentType) {
  const config = DOCUMENT_TEMPLATE_CONFIG[documentType] || DOCUMENT_TEMPLATE_CONFIG.default;
  const { intro, actionBlock } = config;
  const action = ACTION_BLOCKS[actionBlock] || ACTION_BLOCKS.record_review;

  const attachment = `Please find attached the [Document Type] for your property at [Property Address].`;

  const bodyParts = [attachment];
  if (intro) bodyParts.push(intro);
  bodyParts.push(action);

  return `Hi [Tenant Name(s)],\n\n${bodyParts.join('\n\n')}\n\nKind regards,\n[Landlord Name]`;
}

const supportedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];

const fallbackRecipient = { name: '', email: '' };

function prettyBytes(bytes) {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function downloadBase64Pdf(base64, filename) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ─── Auth nudge dialogs ────────────────────────────────────────────────────

function FeatureList({ items }) {
  return (
    <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <CheckCircle className="h-4 w-4 shrink-0 mt-0.5 text-green-600" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function LoginRequiredDialog({ open, onUseOtherEmail }) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-[90vw] sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>This email is registered with Siyak</DialogTitle>
        </DialogHeader>
        <p className="text-base text-muted-foreground mt-1">
          To send documents using this email address, you need to be logged in. This keeps your account secure — it means no one else can send documents on your behalf.
        </p>
        <FeatureList items={[
          'Your name and email are always pre-filled',
          'Your delivery history saved automatically — revisit any receipt any time',
          'Acknowledgement tracking across all tenants',
          'One-click reminder sending',
        ]} />
        <div className="flex flex-col gap-2 mt-4">
          <Button className="w-full" onClick={() => navigate('/login?redirect=/send-documents')}>
            Log in to continue
          </Button>
          <Button className="w-full" variant="outline" onClick={onUseOtherEmail}>
            Use a different email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RegistrationNudgeDialog({ open, onContinue, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Protect your email address</DialogTitle>
        </DialogHeader>
        <p className="text-base text-muted-foreground mt-1">
          Anyone could send a document using your email address unless you register. A free Siyak account links your email to your identity so only you can use it for document delivery.
        </p>
        <FeatureList items={[
          'Full document delivery history in your dashboard',
          'Your delivery history saved automatically — revisit any receipt any time',
          'Acknowledgement tracking — see when tenants confirm receipt',
          'One-click reminder sending for pending acknowledgements',
          'Always get the latest template versions automatically',
        ]} />
        <div className="flex flex-col gap-2 mt-4">
          <Button className="w-full" asChild>
            <Link to="/register?role=landlord">Create a free account</Link>
          </Button>
          <Button className="w-full" variant="outline" onClick={() => { onClose(); onContinue(); }}>
            Continue without registering
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateDownloadDialog({ open, onClose, onDownload }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download the delivery record template</DialogTitle>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground pt-0.5">
            <Users className="h-3.5 w-3.5 shrink-0" />
            Used by over 1,000 UK landlords
          </p>
        </DialogHeader>
        <p className="text-base text-muted-foreground mt-1">
          You can download this now, but registered landlords always have access to the latest version — we update it whenever regulations change.
        </p>
        <FeatureList items={[
          'Always get the current version — no hunting for updates',
          'Your delivery history saved automatically — revisit any receipt any time',
          'Acknowledgement tracking and one-click reminders in your dashboard',
          'Free, no card required',
        ]} />
        <div className="flex flex-col gap-2 mt-4">
          <Button className="w-full" asChild>
            <Link to="/register?role=landlord">Create a free account</Link>
          </Button>
          <Button className="w-full" variant="outline" onClick={() => { onClose(); onDownload(); }}>
            Download without registering
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const SendDocuments = () => {
  useEffect(() => { document.title = 'Send Documents - SIYAK'; }, []);

  const { user, isLandlord } = useAuth();
  const isLoggedInLandlord = isLandlord();

  // Dialog state (React state only — no localStorage)
  const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);
  const [registrationNudgeOpen, setRegistrationNudgeOpen] = useState(false);
  const [templateDownloadOpen, setTemplateDownloadOpen] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [emailBlocked, setEmailBlocked] = useState(false);
  const [pendingTemplateDownload, setPendingTemplateDownload] = useState(null);

  const [propertyAddress, setPropertyAddress] = useState('');

  const [documentType, setDocumentType] = useState('');
  const [useDefaultDocument, setUseDefaultDocument] = useState(true);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [recipients, setRecipients] = useState([{ ...fallbackRecipient }]);
  const [landlordName, setLandlordName] = useState('');
  const [landlordEmail, setLandlordEmail] = useState('');
  const [coveringMessage, setCoveringMessage] = useState('');
  const [lastGeneratedTemplate, setLastGeneratedTemplate] = useState('');
  const [coveringLocked, setCoveringLocked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [addressWarningActive, setAddressWarningActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  // success must be declared before the scroll effect so the dependency array
  // captures the real state binding rather than an uninitialised variable.
  const [success, setSuccess] = useState(null);

  // Scroll to top when the receipt page appears after a successful delivery.
  useEffect(() => {
    if (success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success]);

  // Pre-fill sender fields from logged-in landlord account.
  useEffect(() => {
    if (isLoggedInLandlord && user) {
      setLandlordEmail(user.email || '');
      setLandlordName((prev) => prev || user.name || '');
    }
  }, [isLoggedInLandlord, user]);

  // On blur of sender email: check if the typed email belongs to a registered landlord.
  const checkEmailRegistered = useCallback(async (email) => {
    if (isLoggedInLandlord) return;
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    try {
      const res = await axios.get(toApiUrl('/api/public/document-delivery/check-email-registered'), {
        params: { email: trimmed },
      });
      if (res.data?.registered) {
        setEmailBlocked(true);
        setLoginRequiredOpen(true);
      } else if (!nudgeDismissed) {
        setRegistrationNudgeOpen(true);
      }
    } catch (_) {
      // fail silently — don't block the user on a check failure
    }
  }, [isLoggedInLandlord, nudgeDismissed]);

  const [defaultDocStatus, setDefaultDocStatus] = useState(null);
  const [defaultDocStatusLoading, setDefaultDocStatusLoading] = useState(false);

  const isHostedDocument = hostedDocuments.some((d) => d.label === documentType);
  const isUsingDefaultDocument = isHostedDocument && useDefaultDocument && !uploadFile;

  const composedAddress = propertyAddress.trim();

  const recipientLabel = useMemo(() => {
    const names = recipients.map((r) => r.name.trim()).filter(Boolean);
    return names.length ? names.join(' & ') : 'there';
  }, [recipients]);

  const selectedNote = documentType
    ? (allDocumentOptions.find((doc) => doc.label === documentType)?.note || '')
    : '';
  const selectedDefaultDocument = useMemo(() => hostedDocuments.find((d) => d.label === documentType), [documentType]);
  const hostedDocumentPreviewUrl = useMemo(() => {
    if (!isHostedDocument || !documentType) return '';
    return `${toApiUrl('/api/public/document-delivery/hosted-document')}?document_type=${encodeURIComponent(documentType)}`;
  }, [documentType, isHostedDocument]);

  const generatedTemplate = useMemo(() => {
    if (!documentType) return '';
    const base = buildTemplate(documentType);
    const propertyValue = composedAddress || '[Property Address]';
    const landlordValue = landlordName.trim() || 'Your landlord';
    return base
      .replaceAll('[Tenant Name(s)]', recipientLabel)
      .replaceAll('[Property Address]', propertyValue)
      .replaceAll('[Landlord Name]', landlordValue)
      .replaceAll('[Document Type]', documentType);
  }, [documentType, composedAddress, landlordName, recipientLabel]);

  useEffect(() => {
    if (!documentType) {
      setLastGeneratedTemplate('');
      setCoveringMessage('');
      setCoveringLocked(false);
      setUseDefaultDocument(true);
      return;
    }

    const isStillAuto = !coveringLocked || coveringMessage === lastGeneratedTemplate;
    if (isStillAuto) {
      setCoveringMessage(generatedTemplate);
      setLastGeneratedTemplate(generatedTemplate);
      setCoveringLocked(false);
    } else {
      setLastGeneratedTemplate(generatedTemplate);
    }
  }, [documentType, generatedTemplate, coveringLocked, coveringMessage, lastGeneratedTemplate]);

  useEffect(() => {
    if (!isUsingDefaultDocument || !documentType) {
      setDefaultDocStatus(null);
      setDefaultDocStatusLoading(false);
      return;
    }

    let cancelled = false;
    setDefaultDocStatusLoading(true);
    axios.get(toApiUrl('/api/public/document-delivery/hosted-document-status'), {
      params: { document_type: documentType },
    }).then((response) => {
      if (!cancelled) {
        setDefaultDocStatus(response.data || null);
        setDefaultDocStatusLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setDefaultDocStatus(null);
        setDefaultDocStatusLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isUsingDefaultDocument, documentType]);

  const formattedLastGovDownload = useMemo(() => {
    if (!defaultDocStatus?.last_downloaded_at) return 'Not downloaded yet in this environment';
    const parsed = new Date(defaultDocStatus.last_downloaded_at);
    if (Number.isNaN(parsed.getTime())) return defaultDocStatus.last_downloaded_at;
    return parsed.toLocaleString();
  }, [defaultDocStatus]);

  const defaultDocSyncState = useMemo(() => {
    if (defaultDocStatusLoading) {
      return {
        badge: 'Checking PDF status',
        progress: 55,
        progressClass: 'bg-navy-400',
        note: 'Checking gov.uk refresh status...',
      };
    }

    if (defaultDocStatus?.has_local_fallback) {
      if (defaultDocStatus?.last_downloaded_at) {
        const parsed = new Date(defaultDocStatus.last_downloaded_at);
        const elapsedMs = Number.isNaN(parsed.getTime()) ? 0 : (Date.now() - parsed.getTime());
        const dailyProgress = Math.max(0, Math.min(100, Math.round((elapsedMs / (24 * 60 * 60 * 1000)) * 100)));
        return {
          badge: 'Default PDF ready',
          progress: dailyProgress,
          progressClass: 'bg-navy-700',
          note: `Last downloaded from gov.uk: ${formattedLastGovDownload}`,
        };
      }

      return {
        badge: 'Default PDF ready',
        progress: 100,
        progressClass: 'bg-navy-700',
        note: 'Using local default PDF cache (no gov.uk download recorded yet).',
      };
    }

    return {
      badge: 'Default PDF unavailable',
      progress: 0,
      progressClass: 'bg-red-500',
      note: 'Could not prepare default PDF. Upload your own PDF to continue.',
    };
  }, [defaultDocStatusLoading, defaultDocStatus, formattedLastGovDownload]);

  const validRecipientsCount = useMemo(
    () => recipients.filter((r) => r.name.trim() && r.email.trim()).length,
    [recipients],
  );

  const hasPreparedDocument = useMemo(() => {
    if (!documentType) return false;
    if (isUsingDefaultDocument) return Boolean(defaultDocStatus?.has_local_fallback);
    return Boolean(uploadFile);
  }, [documentType, isUsingDefaultDocument, defaultDocStatus, uploadFile]);

  const completionScore = useMemo(() => {
    let score = 0;
    if (propertyAddress.trim()) score += 1;
    if (hasPreparedDocument) score += 1;
    if (validRecipientsCount > 0) score += 1;
    if (landlordName.trim() && landlordEmail.trim()) score += 1;
    if (coveringMessage.trim()) score += 1;
    return score;
  }, [propertyAddress, hasPreparedDocument, validRecipientsCount, landlordName, landlordEmail, coveringMessage]);

  const updateRecipient = (index, field, value) => {
    setRecipients((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const addRecipient = () => {
    if (recipients.length >= 6) return;
    setRecipients((prev) => [...prev, { ...fallbackRecipient }]);
  };

  const removeRecipient = (index) => {
    if (index === 0) return;
    setRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  const setFieldError = (key, message) => {
    setErrors((prev) => ({ ...prev, [key]: message }));
  };

  const clearFieldError = (key) => {
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const clearSelectedDocument = () => {
    if (isUsingDefaultDocument) {
      setUseDefaultDocument(false);
      setDragOver(false);
    } else if (uploadFile) {
      setUploadFile(null);
    } else {
      setDocumentType('');
      setUseDefaultDocument(true);
    }

    clearFieldError('documentType');
    clearFieldError('file');
  };

  const validate = () => {
    const next = {};
    if (!propertyAddress.trim()) next.propertyAddress = 'Missing required field';
    else if (addressWarningActive) next.propertyAddress = 'Incomplete address — include a house or flat number, or dismiss the warning to continue.';

    if (!documentType) next.documentType = 'Missing required field';
    if (!isUsingDefaultDocument && !uploadFile) next.file = 'Missing required field';

    recipients.forEach((r, idx) => {
      const fullName = validatePersonFullName(r.name, `Tenant ${idx + 1} Full Name`);
      if (!fullName.valid) next[`recipient_name_${idx}`] = fullName.error;
      const email = r.email.trim();
      if (!email) {
        next[`recipient_email_${idx}`] = 'Missing required field';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        next[`recipient_email_${idx}`] = 'Please enter a valid email address';
      }
    });

    const senderFullName = validatePersonFullName(landlordName, 'Sender Full Name');
    if (!senderFullName.valid) next.landlordName = senderFullName.error;
    if (!landlordEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(landlordEmail.trim())) {
      next.landlordEmail = 'Please enter a valid email address';
    } else if (emailBlocked) {
      next.landlordEmail = 'Log in to send documents using this registered email address.';
    }
    if (!coveringMessage.trim()) next.coveringMessage = 'An email message is required.';
    if (coveringMessage.length > 2000) next.coveringMessage = 'Message must be 2000 characters or fewer.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onFilePicked = (fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, file: 'File must be under 10MB' }));
      return;
    }
    if (!supportedTypes.includes(fileObj.type)) {
      setErrors((prev) => ({ ...prev, file: 'Only PDF, DOC, DOCX, JPG and PNG accepted' }));
      return;
    }
    clearFieldError('file');
    setUploadFile(fileObj);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('document_type', documentType);
      payload.append('property_address', composedAddress);
      payload.append('sender_name', validatePersonFullName(landlordName, 'Sender Full Name').value);
      payload.append('sender_email', landlordEmail.trim());
      payload.append('covering_message', coveringMessage);
      payload.append('recipients_json', JSON.stringify(recipients.map((r, index) => ({
        name: validatePersonFullName(r.name, `Tenant ${index + 1} Full Name`).value,
        email: r.email.trim(),
      }))));
      payload.append('website', '');
      if (uploadFile) {
        payload.append('file', uploadFile);
      }

      // Attach ToS acceptance info
      const tosVersion = process.env.REACT_APP_TOS_CURRENT_VERSION || 'v1.1';
      if (!tosAccepted) {
        throw new Error('TOS not accepted');
      }
      payload.append('tos_version_accepted', tosVersion);
      payload.append('tos_accepted_at', new Date().toISOString());

      const response = await axios.post(toApiUrl('/api/public/document-delivery'), payload);
      setSuccess({ ...response.data, landlordEmail: landlordEmail.trim(), recipientNames: recipients.map((r) => r.name.trim()) });
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 429) setSubmitError('Too many requests. Please try again later.');
      else if (status === 502) setSubmitError('The document could not be sent. Please check the email addresses and try again. No delivery record has been generated.');
      else if (status === 503) setSubmitError('This document is temporarily unavailable. Please upload your own copy.');
      else if (typeof detail === 'string') setSubmitError(detail);
      else if (detail?.message) setSubmitError(detail.message);
      else setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <Layout>
        <div className="min-h-screen bg-navy-50 py-10">
          <div className="max-w-3xl mx-auto px-4 space-y-4">
            <Card>
              <CardContent className="pt-8 pb-8 text-center space-y-4">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                <h1 className="font-heading text-2xl font-semibold text-navy-900">Document sent successfully</h1>
                <p className="text-lg font-bold text-royal-700">{success.delivery_reference || success.reference}</p>
                <p>Sent to: {success.recipientNames.join(', ')}</p>
                <p>A delivery record has been sent to {success.landlordEmail}</p>
                <p className="text-sm text-muted-foreground">SIYAK facilitates document delivery and record-keeping. Landlords remain responsible for ensuring compliance with applicable legal service requirements.</p>
                <div className="space-y-2">
                  {(success.delivery_record_pdfs || success.confirmation_pdfs || []).map((pdf, index) => (
                    <Button
                      key={pdf.filename}
                      variant="outline"
                      onClick={() => downloadBase64Pdf(pdf.data, pdf.filename)}
                      className="w-full min-w-0"
                    >
                      <Download className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">Download delivery record</span>
                    </Button>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-4 text-sm text-left">
                  Download your delivery records now. Once you leave this page they will only be available in your inbox at {success.landlordEmail}. Keep reference {success.delivery_reference || success.reference} for your records.
                </div>
                <p className="text-sm text-muted-foreground">If Resend reports a bounced recipient address, a failure notice will be sent to your sender email. For support, use the Contact Us form.</p>
              </CardContent>
            </Card>

            <Card className="bg-royal-700 text-white">
              <CardContent className="pt-6 pb-6 space-y-4">
                <p className="font-semibold">Want to track all your property documents, rent, and tenancy records in one place?</p>
                {isLoggedInLandlord ? (
                  <Button asChild variant="secondary">
                    <Link to="/landlord?tab=document-deliveries">View in your delivery history →</Link>
                  </Button>
                ) : (
                  <Button asChild variant="secondary">
                    <Link to="/register?role=landlord">Create a SIYAK account</Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-navy-50 via-white to-navy-50 py-6 md:py-8">
        <div className="max-w-4xl mx-auto px-4 space-y-4">
          <ManualTemplateBanner
            href={`${API_URL}/api/public/manual-template/download`}
            isLoggedIn={isLoggedInLandlord}
            onNudge={(proceed) => {
              setPendingTemplateDownload(() => proceed);
              setTemplateDownloadOpen(true);
            }}
          />
          <Card className="border-navy-100 shadow-sm">
            <CardHeader className="border-b bg-gradient-to-r from-white to-navy-50/70 py-6 md:py-7">
              <CardTitle className="font-heading text-3xl text-navy-900 md:text-[2rem]">Send Documents</CardTitle>
              <CardDescription className="mt-1 text-sm">
                A structured five-step flow with delivery records and downloadable PDFs.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 pb-5 md:pb-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
                <form id="send-documents-form" onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:col-span-8">
                <section className="space-y-4 rounded-xl border border-navy-100 bg-white/90 p-4 md:p-5">
                  <h2 className="font-semibold text-navy-900">Section 1 - Property details</h2>
                  <AddressInput
                    defaultValue={propertyAddress}
                    label="Property address"
                    onAddressSelect={(addressObject) => {
                      setPropertyAddress(addressObject?.formatted || '');
                      clearFieldError('propertyAddress');
                    }}
                    onWarning={(active) => {
                      setAddressWarningActive(active);
                      if (!active) clearFieldError('propertyAddress');
                    }}
                    required={true}
                  />
                  {errors.propertyAddress && <p className="text-sm text-red-600">{errors.propertyAddress}</p>}
                </section>

                <section className="space-y-4 rounded-xl border border-navy-100 bg-white/90 p-4 md:p-5">
                  <h2 className="font-semibold text-navy-900">Section 2 - Document</h2>
                  <Label>Document type</Label>
                  <select
                    className={`w-full rounded-md border px-3 py-2 text-sm ${errors.documentType ? 'border-red-500' : ''}`}
                    value={documentType}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDocumentType(next);
                      clearFieldError('documentType');
                      setUseDefaultDocument(hostedDocuments.some((doc) => doc.label === next));
                      if (hostedDocuments.some((doc) => doc.label === next)) {
                        setUploadFile(null);
                        clearFieldError('file');
                      }
                    }}
                  >
                    <option value="">Select a document</option>
                    <optgroup label="Standard Documents (hosted by SIYAK - no upload needed)">
                      {hostedDocuments.map((doc) => <option key={doc.label} value={doc.label}>{doc.label}</option>)}
                    </optgroup>
                    <optgroup label="Upload Your Own Document">
                      {uploadDocuments.map((doc) => <option key={doc.label} value={doc.label}>{doc.label}</option>)}
                    </optgroup>
                  </select>
                  {errors.documentType && <p className="text-sm text-red-600">{errors.documentType}</p>}
                  {selectedNote && (
                    <div className="my-3 rounded-lg border border-navy-200 bg-white px-4 py-3">
                      <p className="text-sm leading-relaxed text-navy-800">{selectedNote}</p>
                    </div>
                  )}
                  {documentType && isUsingDefaultDocument && (
                    <div className="rounded-lg border border-navy-200 bg-slate-50/80 p-4 space-y-4">
                      {isHostedDocument && (
                        <div className="rounded-md border border-navy-200 bg-white px-3 py-2">
                          {defaultDocStatus?.has_local_fallback ? (
                            <div className="flex items-center justify-between gap-2">
                              <a
                                href={hostedDocumentPreviewUrl}
                                  download={defaultDocStatus?.filename || selectedDefaultDocument?.fileName || 'document.pdf'}
                                className="truncate text-sm font-medium text-navy-800 underline decoration-navy-300 underline-offset-2"
                                title="Download PDF"
                              >
                                {defaultDocStatus?.filename || selectedDefaultDocument?.fileName || 'document.pdf'}
                              </a>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={clearSelectedDocument}
                                aria-label="Remove default document and switch to upload"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <p className="text-sm text-navy-700">Preparing default PDF...</p>
                          )}
                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-navy-100">
                            <div
                              className={`h-full transition-all duration-300 ${defaultDocSyncState.progressClass}`}
                              style={{ width: `${defaultDocSyncState.progress}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs text-navy-600">{defaultDocSyncState.note}</p>
                        </div>
                      )}

                      {isHostedDocument && (
                        <div className="flex items-center justify-end gap-3 flex-wrap text-xs text-navy-600">
                          {selectedDefaultDocument?.govUrl && (
                            <a
                              href={selectedDefaultDocument.govUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-navy-700 underline"
                            >
                              View source on gov.uk
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {documentType && !isUsingDefaultDocument && (
                    <div
                      className={`rounded-lg border border-dashed p-4 ${dragOver ? 'border-royal-500 bg-royal-50' : 'border-navy-300 bg-white'}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        onFilePicked(e.dataTransfer.files?.[0]);
                      }}
                    >
                      <div className="space-y-2">
                        {isHostedDocument && selectedDefaultDocument?.govUrl && (
                          <a
                            href={selectedDefaultDocument.govUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-block text-xs text-navy-700 underline"
                          >
                            View source on gov.uk
                          </a>
                        )}
                        <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, JPG or PNG up to 10MB.</p>
                        <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => onFilePicked(e.target.files?.[0])} />
                      </div>
                      {uploadFile && (
                        <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-navy-200 bg-navy-50 px-2 py-1.5">
                          <p className="text-sm">{uploadFile.name} ({prettyBytes(uploadFile.size)})</p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setUploadFile(null);
                              clearFieldError('file');
                            }}
                            aria-label="Remove uploaded file"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {!uploadFile && isHostedDocument && (
                        <p className="text-xs text-muted-foreground mt-3">Download the default PDF above first if you want to edit and re-upload it.</p>
                      )}
                      {errors.file && <p className="text-sm text-red-600 mt-2">{errors.file}</p>}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">For privacy, SIYAK does not retain copies of sent documents.</p>
                </section>

                <section className="space-y-4 rounded-xl border border-navy-100 bg-white/90 p-5 md:p-6">
                  <h2 className="text-lg font-semibold text-navy-900">Recipients</h2>
                  {recipients.map((recipient, index) => (
                    <div key={`recipient-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-2.5 items-start">
                      <div>
                        <Label className="text-sm font-medium text-navy-900">Tenant Full Name</Label>
                        <Input value={recipient.name} onChange={(e) => updateRecipient(index, 'name', e.target.value)} placeholder="Full Name (e.g. John Smith)" className={errors[`recipient_name_${index}`] ? 'h-11 text-base border-red-500' : 'h-11 text-base'} />
                        {errors[`recipient_name_${index}`] && <p className="text-sm text-red-600">{errors[`recipient_name_${index}`]}</p>}
                      </div>
                      <div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-sm font-medium text-navy-900">Tenant email address</Label>
                            <Input value={recipient.email} onChange={(e) => updateRecipient(index, 'email', e.target.value)} className={errors[`recipient_email_${index}`] ? 'h-11 text-base border-red-500' : 'h-11 text-base'} />
                          </div>
                          {index > 0 && (
                            <Button type="button" variant="ghost" className="mt-6" onClick={() => removeRecipient(index)}>
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {errors[`recipient_email_${index}`] && <p className="text-sm text-red-600">{errors[`recipient_email_${index}`]}</p>}
                      </div>
                    </div>
                  ))}
                  {recipients.length < 6 && (
                    <button type="button" onClick={addRecipient} className="inline-flex items-center gap-1 text-sm font-medium text-royal-700 underline">
                      <Plus className="h-3 w-3" /> Add another tenant
                    </button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <div>
                      <Label className="text-sm font-medium text-navy-900">Sender Full Name</Label>
                      <Input
                        value={landlordName}
                        onChange={(e) => setLandlordName(e.target.value)}
                        placeholder="Full Name (e.g. John Smith)"
                        className={errors.landlordName ? 'h-11 text-base border-red-500' : 'h-11 text-base'}
                      />
                      {errors.landlordName && <p className="text-sm text-red-600">{errors.landlordName}</p>}
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-navy-900">Sender email address</Label>
                      <div className="relative">
                        <Input
                          value={landlordEmail}
                          onChange={(e) => {
                            setLandlordEmail(e.target.value);
                            if (emailBlocked) setEmailBlocked(false);
                          }}
                          onBlur={(e) => checkEmailRegistered(e.target.value)}
                          disabled={isLoggedInLandlord}
                          className={`h-11 text-base ${errors.landlordEmail ? 'border-red-500' : ''} ${isLoggedInLandlord ? 'pr-9' : ''}`}
                        />
                        {isLoggedInLandlord && (
                          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        )}
                      </div>
                      {isLoggedInLandlord
                        ? <p className="mt-1 text-xs text-green-700 font-medium">Verified account</p>
                        : <p className="mt-2 text-sm text-muted-foreground">Your delivery record for reference will be sent here.</p>
                      }
                      {errors.landlordEmail && <p className="text-sm text-red-600">{errors.landlordEmail}</p>}
                    </div>
                  </div>
                </section>

                <section className="space-y-4 rounded-xl border border-navy-100 bg-white/90 p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-navy-900">Email message (sent to tenants)</h2>
                    {coveringLocked && (
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => {
                          setCoveringMessage(generatedTemplate);
                          setLastGeneratedTemplate(generatedTemplate);
                          setCoveringLocked(false);
                        }}
                      >
                        Reset to recommended wording
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">This message will be sent to your tenants as written. Please review it carefully.</p>
                  <Textarea
                    value={coveringMessage}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCoveringMessage(value);
                      setCoveringLocked(value !== lastGeneratedTemplate);
                    }}
                    rows={12}
                    maxLength={2000}
                    className={errors.coveringMessage ? 'min-h-[18rem] text-base border-red-500' : 'min-h-[18rem] text-base'}
                  />
                  <p className="text-sm text-muted-foreground">{coveringMessage.length}/2000</p>
                  {errors.coveringMessage && <p className="text-sm text-red-600">{errors.coveringMessage}</p>}
                </section>

                <input type="text" name="website" autoComplete="off" tabIndex={-1} className="hidden" />

                {submitError && <p className="text-sm text-red-600">{submitError}</p>}

                </form>

                <aside className="lg:col-span-4 space-y-4 lg:sticky lg:top-20 lg:self-start">
                  <Card className="border-navy-100 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base text-navy-900">Delivery Summary</CardTitle>
                      <CardDescription>Track completion before sending.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <div className="h-1.5 w-full overflow-hidden rounded bg-navy-100">
                        <div className="h-full bg-navy-700 transition-all duration-300" style={{ width: `${(completionScore / 5) * 100}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-navy-800">
                        <span>Form progress</span>
                        <span className="font-medium">{completionScore}/5 complete</span>
                      </div>

                      <div className="space-y-2 pt-1">
                        <div className="flex items-center justify-between text-navy-700">
                          <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Document</span>
                          <span>{hasPreparedDocument ? 'Ready' : 'Pending'}</span>
                        </div>
                        <div className="flex items-center justify-between text-navy-700">
                          <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> Recipients</span>
                          <span>{validRecipientsCount}</span>
                        </div>
                        <div className="flex items-center justify-between text-navy-700">
                          <span className="inline-flex items-center gap-2"><Send className="h-4 w-4" /> Sender</span>
                          <span>{landlordName.trim() && landlordEmail.trim() ? 'Ready' : 'Pending'}</span>
                        </div>
                      </div>

                      <div className="pt-4">
                        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                          This tool records that a document was sent via SIYAK on the date shown. Delivery depends on the recipient&apos;s email system and cannot be guaranteed. Sender identity is not verified. This is not legal advice.<br /><strong>For privacy, SIYAK does not retain copies of sent documents.</strong>
                        </p>
                        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                          <strong>SIYAK facilitates document delivery and record-keeping. Landlords remain responsible for ensuring compliance with applicable legal service requirements.</strong>
                        </p>
                        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                          We record IP address and approximate location to prevent misuse and maintain audit logs.
                        </p>

                        <label className="flex items-start gap-2 mb-3 text-sm">
                          <input type="checkbox" checked={tosAccepted} onChange={(e) => setTosAccepted(e.target.checked)} className="mt-1" />
                          <span>I have read and accept the <a href="/terms" className="underline">Terms</a> and understand this record relates to document delivery via SIYAK.</span>
                        </label>

                        <Button
                          type="submit"
                          form="send-documents-form"
                          disabled={isSubmitting || completionScore < 5 || !tosAccepted}
                          size="sm"
                          className="w-full min-h-10 min-w-0"
                        >
                          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />}
                          <span className="truncate">
                            {isSubmitting ? (
                              <>
                                <span className="sm:hidden">Sending…</span>
                                <span className="hidden sm:inline">Sending documents…</span>
                              </>
                            ) : (
                              'Send & Get Delivery Record'
                            )}
                          </span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                </aside>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <LoginRequiredDialog
        open={loginRequiredOpen}
        onUseOtherEmail={() => {
          setLoginRequiredOpen(false);
          setEmailBlocked(false);
          setLandlordEmail('');
        }}
      />

      <RegistrationNudgeDialog
        open={registrationNudgeOpen}
        onClose={() => { setRegistrationNudgeOpen(false); setNudgeDismissed(true); }}
        onContinue={() => setNudgeDismissed(true)}
      />

      <TemplateDownloadDialog
        open={templateDownloadOpen}
        onClose={() => { setTemplateDownloadOpen(false); setPendingTemplateDownload(null); }}
        onDownload={() => {
          setTemplateDownloadOpen(false);
          if (pendingTemplateDownload) pendingTemplateDownload();
          setPendingTemplateDownload(null);
        }}
      />
    </Layout>
  );
};
